import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export const useTableParams = (defaultLimit = 20) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const params = useMemo(() => {
    return Object.fromEntries(searchParams.entries())
  }, [searchParams])

  const page = Number(searchParams.get('page') || 1)
  const limit = Number(searchParams.get('limit') || defaultLimit)
  const query = searchParams.get('searchQuery') || searchParams.get('search') || searchParams.get('query') || ''

  const patchSearchParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        mutate(next)
        return next
      })
    },
    [setSearchParams]
  )

  /** `PatientPagination` passes zero-based page index. */
  const setPageFromPaginationIndex = useCallback(
    (zeroBasedPageIndex: number) => {
      patchSearchParams(n => {
        n.set('page', String(zeroBasedPageIndex + 1))
      })
    },
    [patchSearchParams]
  )

  /** Updates `limit` and resets to first page (for `PatientPagination` `onPageSizeChange`). */
  const setPageSize = useCallback(
    (size: number) => {
      patchSearchParams(n => {
        n.set('limit', String(size))
        n.set('page', '1')
      })
    },
    [patchSearchParams]
  )

  /** Sets `searchQuery` (or clears it) and resets to page 1. */
  const setSearchQuery = useCallback(
    (value: string) => {
      patchSearchParams(n => {
        const trimmed = value.trim()
        if (trimmed) n.set('searchQuery', trimmed)
        else n.delete('searchQuery')
        n.set('page', '1')
      })
    },
    [patchSearchParams]
  )

  /**
   * Set or remove a single list filter (e.g. `status`). Empty value removes the key. Resets to page 1.
   */
  const setListFilterParam = useCallback(
    (key: string, value: string) => {
      patchSearchParams(n => {
        const v = value.trim()
        if (v) n.set(key, v)
        else n.delete(key)
        n.set('page', '1')
      })
    },
    [patchSearchParams]
  )

  return {
    limit,
    page,
    params,
    patchSearchParams,
    query,
    searchParams,
    setListFilterParam,
    setPageFromPaginationIndex,
    setPageSize,
    setSearchParams,
    setSearchQuery
  }
}
